"use client"

import React, { useEffect, useRef, useCallback } from 'react'
import { 
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  CLEAR_EDITOR_COMMAND,
  CLEAR_HISTORY_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  EditorState,
  LexicalEditor,
  TextFormatType,
  DecoratorNode,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND
} from 'lexical'

import {
  LexicalComposer,
  InitialConfigType
} from '@lexical/react/LexicalComposer'

import {
  ContentEditable
} from '@lexical/react/LexicalContentEditable'

import {
  LexicalErrorBoundary
} from '@lexical/react/LexicalErrorBoundary'

import {
  HistoryPlugin
} from '@lexical/react/LexicalHistoryPlugin'

import {
  RichTextPlugin
} from '@lexical/react/LexicalRichTextPlugin'

import {
  OnChangePlugin
} from '@lexical/react/LexicalOnChangePlugin'

import {
  useLexicalComposerContext
} from '@lexical/react/LexicalComposerContext'

import {
  AutoLinkPlugin
} from '@lexical/react/LexicalAutoLinkPlugin'

import {
  LinkPlugin
} from '@lexical/react/LexicalLinkPlugin'

import {
  ListPlugin
} from '@lexical/react/LexicalListPlugin'

import {
  MarkdownShortcutPlugin
} from '@lexical/react/LexicalMarkdownShortcutPlugin'

import {
  AutoFocusPlugin
} from '@lexical/react/LexicalAutoFocusPlugin'

import {
  HeadingNode,
  QuoteNode,
  $createHeadingNode,
  $isHeadingNode,
  HeadingTagType
} from '@lexical/rich-text'

import {
  ListItemNode,
  ListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode
} from '@lexical/list'

import {
  CodeNode,
  CodeHighlightNode,
  $createCodeNode,
  $isCodeNode
} from '@lexical/code'

import {
  LinkNode,
  AutoLinkNode,
  $createLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND
} from '@lexical/link'

import {
  $getSelectionStyleValueForProperty,
  $patchStyleText
} from '@lexical/selection'

import { TRANSFORMERS } from '@lexical/markdown'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { PASTE_COMMAND, DROP_COMMAND, COMMAND_PRIORITY_LOW, type DOMConversionMap, type DOMConversionOutput, type DOMExportOutput } from 'lexical'
import { $insertNodes } from 'lexical'

// Minimal className join helper (avoids project-specific imports)
function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough,
  List,
  ListOrdered,
  Code,
  Undo2,
  Redo2,
  Heading1,
  Heading2,
  Heading3,
  Eraser
} from 'lucide-react'

// Editor theme configuration
const theme = {
  ltr: 'ltr',
  rtl: 'rtl',
  placeholder: 'editor-placeholder',
  paragraph: 'editor-paragraph',
  quote: 'editor-quote',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5',
    h6: 'editor-heading-h6',
  },
  list: {
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
  image: 'editor-image',
  link: 'editor-link',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    overflowed: 'editor-text-overflowed',
    hashtag: 'editor-text-hashtag',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
    code: 'editor-text-code',
  },
  code: 'editor-code',
  codeHighlight: {
    atrule: 'editor-tokenAttr',
    attr: 'editor-tokenAttr',
    boolean: 'editor-tokenProperty',
    builtin: 'editor-tokenSelector',
    cdata: 'editor-tokenComment',
    char: 'editor-tokenSelector',
    class: 'editor-tokenFunction',
    'class-name': 'editor-tokenFunction',
    comment: 'editor-tokenComment',
    constant: 'editor-tokenProperty',
    deleted: 'editor-tokenProperty',
    doctype: 'editor-tokenComment',
    entity: 'editor-tokenOperator',
    function: 'editor-tokenFunction',
    important: 'editor-tokenVariable',
    inserted: 'editor-tokenSelector',
    keyword: 'editor-tokenAttr',
    namespace: 'editor-tokenVariable',
    number: 'editor-tokenProperty',
    operator: 'editor-tokenOperator',
    prolog: 'editor-tokenComment',
    property: 'editor-tokenProperty',
    punctuation: 'editor-tokenPunctuation',
    regex: 'editor-tokenVariable',
    selector: 'editor-tokenSelector',
    string: 'editor-tokenSelector',
    symbol: 'editor-tokenProperty',
    tag: 'editor-tokenProperty',
    url: 'editor-tokenOperator',
    variable: 'editor-tokenVariable',
  },
}

// Image node implementation
class ImageNode extends DecoratorNode<React.ReactElement> {
  __src: string
  __altText: string
  __width: number | null
  __height: number | null

  static getType(): string {
    return 'image'
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__key
    )
  }

  constructor(
    src: string,
    altText: string = '',
    width?: number | null,
    height?: number | null,
    key?: string
  ) {
    super(key)
    this.__src = src
    this.__altText = altText
    this.__width = width || null
    this.__height = height || null
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span')
    return span
  }

  updateDOM(): false {
    return false
  }

  setWidthAndHeight(width: number | null, height: number | null): void {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
  }

  decorate(): React.ReactElement {
    return (
      <img
        src={this.__src}
        alt={this.__altText}
        style={{
          width: this.__width || undefined,
          maxWidth: '100%',
          height: 'auto'
        }}
        draggable={false}
      />
    )
  }

  // Ensure HTML export includes <img>
  exportDOM(): DOMExportOutput {
    const element = document.createElement('img')
    element.setAttribute('src', this.__src)
    if (this.__altText) element.setAttribute('alt', this.__altText)
    if (typeof this.__width === 'number') element.width = this.__width
    if (typeof this.__height === 'number') element.height = this.__height
    return { element }
  }

  // Ensure HTML import converts <img> back to ImageNode
  static importDOM(): DOMConversionMap | null {
    const convertImage = (domNode: Node): DOMConversionOutput | null => {
      if (domNode instanceof HTMLImageElement) {
        const src = domNode.getAttribute('src') || ''
        const altText = domNode.getAttribute('alt') || ''
        const widthAttr = domNode.getAttribute('width')
        const heightAttr = domNode.getAttribute('height')
        const width = widthAttr ? parseInt(widthAttr, 10) : undefined
        const height = heightAttr ? parseInt(heightAttr, 10) : undefined
        return { node: $createImageNode({ src, altText, width, height }) }
      }
      return null
    }
    return {
      img: () => ({ conversion: convertImage, priority: 1 as const })
    }
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      height: this.__height,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width,
    }
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, height, src, width } = serializedNode
    const node = $createImageNode({
      altText,
      height,
      src,
      width,
    })
    return node
  }

  getSrc(): string {
    return this.__src
  }

  getAltText(): string {
    return this.__altText
  }
}

interface SerializedImageNode {
  altText: string
  height?: number | null
  src: string
  type: 'image'
  version: 1
  width?: number | null
}

function $createImageNode({
  altText = '',
  height,
  src,
  width,
}: {
  altText?: string
  height?: number | null
  src: string
  width?: number | null
}): ImageNode {
  return new ImageNode(src, altText, width, height)
}

function $isImageNode(node: any): node is ImageNode {
  return node instanceof ImageNode
}

// Toolbar component
function ToolbarPlugin(): React.ReactElement {
  const [editor] = useLexicalComposerContext()
  const [activeFormats, setActiveFormats] = React.useState<Set<string>>(new Set())
  const [canUndo, setCanUndo] = React.useState(false)
  const [canRedo, setCanRedo] = React.useState(false)
  const [blockType, setBlockType] = React.useState('paragraph')
  const [fontSize, setFontSize] = React.useState('15px')

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow()
      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      // Update text format
      const formats = new Set<string>()
      if (selection.hasFormat('bold')) formats.add('bold')
      if (selection.hasFormat('italic')) formats.add('italic')
      if (selection.hasFormat('underline')) formats.add('underline')
      if (selection.hasFormat('strikethrough')) formats.add('strikethrough')
      if (selection.hasFormat('code')) formats.add('code')
      
      setActiveFormats(formats)

      // Update block type
      if ($isHeadingNode(element)) {
        setBlockType(element.getTag())
      } else if ($isListNode(element)) {
        setBlockType(element.getTag())
      } else if ($isCodeNode(element)) {
        setBlockType('code')
      } else {
        setBlockType('paragraph')
      }

      // Update font size
      const fontSize = $getSelectionStyleValueForProperty(selection, 'font-size', '15px')
      setFontSize(fontSize)
    }
  }, [editor])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar()
      })
    })
  }, [editor, updateToolbar])

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, _newEditor) => {
        updateToolbar()
        return false
      },
      1
    )
  }, [editor, updateToolbar])

  useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload)
        return false
      },
      1
    )
  }, [editor])

  useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload)
        return false
      },
      1
    )
  }, [editor])

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)
  }

  const formatHeading = (headingType: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode()
        const element = anchorNode.getTopLevelElementOrThrow()
        
        if ($isHeadingNode(element) && element.getTag() === headingType) {
          // Convert to paragraph if already the same heading type
          const newParagraph = $createParagraphNode()
          element.replace(newParagraph, true)
        } else {
          // Convert to heading
          const newHeading = $createHeadingNode(headingType)
          element.replace(newHeading, true)
        }
      }
    })
  }

  const insertList = (type: 'ul' | 'ol') => {
    if (type === 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    }
  }

  // Toggle list: if already in the same list type, remove it; if in other list type, switch; otherwise insert
  const toggleList = (type: 'ul' | 'ol') => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return
      const anchorNode = selection.anchor.getNode()
      const element = anchorNode.getTopLevelElementOrThrow()

      if ($isListNode(element)) {
        const currentTag = element.getTag()
        if (currentTag === type) {
          // Remove current list
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
          // Switch list type
          if (type === 'ul') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
        }
      } else {
        // Not in a list: insert the requested type
        if (type === 'ul') {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        } else {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
      }
    })
  }

  const clearFormatting = () => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode()
        const element = anchorNode.getTopLevelElementOrThrow()

        // Remove inline text formats
        ;(['bold', 'italic', 'underline', 'strikethrough', 'code'] as TextFormatType[]).forEach((fmt) => {
          if (selection.hasFormat(fmt)) {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, fmt)
          }
        })

        // Remove link formatting
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)

        // Clear common inline styles
        $patchStyleText(selection, {
          'font-size': '',
          'color': '',
          'background-color': '',
        })

        // If inside a list, remove the list
        if ($isListNode(element)) {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        }

        // Convert block types back to paragraph (heading/code/quote)
        if ($isHeadingNode(element) || $isCodeNode(element) || element.getType() === 'quote') {
          const newParagraph = $createParagraphNode()
          element.replace(newParagraph, true)
          newParagraph.select()
        }
      }
    })
  }

  const undo = () => {
    editor.dispatchCommand(UNDO_COMMAND, undefined)
  }

  const redo = () => {
    editor.dispatchCommand(REDO_COMMAND, undefined)
  }

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted/10">
      {/* Undo/Redo */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={undo}
          disabled={!canUndo}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            !canUndo && "opacity-50 cursor-not-allowed"
          )}
          title="Undo"
        >
          <Undo2 className="h-4 w-4" />
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            !canRedo && "opacity-50 cursor-not-allowed"
          )}
          title="Redo"
        >
          <Redo2 className="h-4 w-4" />
        </button>
      </div>

      {/* Text Formatting */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={() => formatText('bold')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            activeFormats.has('bold') && "bg-muted"
          )}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          onClick={() => formatText('italic')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            activeFormats.has('italic') && "bg-muted"
          )}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          onClick={() => formatText('underline')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            activeFormats.has('underline') && "bg-muted"
          )}
          title="Underline"
        >
          <Underline className="h-4 w-4" />
        </button>
        <button
          onClick={() => formatText('strikethrough')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            activeFormats.has('strikethrough') && "bg-muted"
          )}
          title="Strikethrough"
        >
          <Strikethrough className="h-4 w-4" />
        </button>
        <button
          onClick={() => formatText('code')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            activeFormats.has('code') && "bg-muted"
          )}
          title="Inline Code"
        >
          <Code className="h-4 w-4" />
        </button>
      </div>

      {/* Headings */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={() => formatHeading('h1')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            blockType === 'h1' && "bg-muted"
          )}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </button>
        <button
          onClick={() => formatHeading('h2')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            blockType === 'h2' && "bg-muted"
          )}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => formatHeading('h3')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            blockType === 'h3' && "bg-muted"
          )}
          title="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </button>
      </div>

      {/* Lists */}
      <div className="flex items-center gap-1 mr-2">
        <button
          onClick={() => toggleList('ul')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            blockType === 'ul' && "bg-muted"
          )}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          onClick={() => toggleList('ol')}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            blockType === 'ol' && "bg-muted"
          )}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
      </div>

      {/* Insert options removed: paste/link auto-detection handles these */}

      {/* Clear Formatting */}
      <div className="flex items-center gap-1">
        <button
          onClick={clearFormatting}
          className="p-1.5 rounded hover:bg-muted transition-colors"
          title="Clear Formatting"
        >
          <Eraser className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// Auto-link matchers
const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/

const EMAIL_MATCHER =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/

const MATCHERS = [
  (text: string) => {
    const match = URL_MATCHER.exec(text)
    if (match === null) {
      return null
    }
    const fullMatch = match[0]
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
      attributes: { rel: 'noreferrer', target: '_blank' },
    }
  },
  (text: string) => {
    const match = EMAIL_MATCHER.exec(text)
    if (match === null) {
      return null
    }
    const fullMatch = match[0]
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: `mailto:${fullMatch}`,
    }
  },
]

// Plugin to add missing imports for history commands
function HistoryStatePlugin(): null {
  const [editor] = useLexicalComposerContext()

  return null
}

// Main editor component
interface LexicalRichTextEditorProps {
  initialValue?: string
  onChange?: (html: string, plainText: string, editorState: EditorState) => void
  placeholder?: string
  className?: string
  readOnly?: boolean
  autoFocus?: boolean
  label?: string
  id?: string
}

export function LexicalRichTextEditor({
  initialValue,
  onChange,
  placeholder = 'Start typing...',
  className,
  readOnly = false,
  autoFocus = false,
  label,
  id,
}: LexicalRichTextEditorProps) {
  const editorStateRef = useRef<EditorState | null>(null)
  const initializedRef = useRef(false)

  const initialConfig: InitialConfigType = {
    namespace: 'LexicalRichTextEditor',
    theme,
    onError: (error: Error) => {
      console.error('Lexical error:', error)
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      LinkNode,
      AutoLinkNode,
      ImageNode,
    ],
    editable: !readOnly,
  }

  const handleEditorChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      editorStateRef.current = editorState
      
      if (onChange) {
        const htmlString = editorState.read(() => {
          return $generateHtmlFromNodes(editor)
        })

        const plainText = editorState.read(() => {
          const root = $getRoot()
          return root.getTextContent()
        })
        
        onChange(htmlString, plainText, editorState)
      }
    },
    [onChange]
  )

  return (
    <div className={cn("border border-input rounded-md", className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      
      <LexicalComposer initialConfig={initialConfig}>
  {!readOnly && <ToolbarPlugin />}
  <ImagePastePlugin />
        {/* Initialize content from initialValue once */}
        {!initializedRef.current && initialValue ? (
          <Initializer initialValue={initialValue} onInitialized={() => { initializedRef.current = true }} />
        ) : null}
        
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                className={cn(
                  "min-h-[200px] p-3 focus:outline-none resize-none",
                  readOnly && "cursor-default"
                )}
                style={{ caretColor: readOnly ? 'transparent' : undefined }}
                onFocus={(e) => {
                  try {
                    // Move selection to end of document on focus
                    const editorElem = e.currentTarget
                    // Defer to allow focus processing first
                    setTimeout(() => {
                      editorElem.dispatchEvent(new Event('mousemove'))
                      editorElem.dispatchEvent(new Event('mouseup'))
                      // Programmatically set selection to end using Lexical API
                      // Requires editor context; run inside update
                    }, 0)
                  } catch {}
                }}
                onMouseUp={() => {
                  try {
                    // Ensure caret stays at end after mouse interactions if focus was just gained
                    // Actual selection control handled below via plugin
                  } catch {}
                }}
              />
            }
            placeholder={
              <div className="absolute top-3 left-3 text-muted-foreground pointer-events-none select-none">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          
          <OnChangePlugin onChange={handleEditorChange} />
          <HistoryPlugin />
          <AutoFocusPlugin defaultSelection={autoFocus ? 'rootEnd' : undefined} />
          <CaretToEndOnFocusPlugin />
          <LinkPlugin />
          <AutoLinkPlugin matchers={MATCHERS} />
          <ListPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        </div>
      </LexicalComposer>
    </div>
  )
}

// Paste/Drop images as data URLs plugin
function ImagePastePlugin(): React.ReactElement | null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: ClipboardEvent) => {
        const files = Array.from(event.clipboardData?.files || [])
        const images = files.filter((f) => f.type.startsWith('image/'))
        if (images.length > 0) {
          event.preventDefault()
          images.forEach((file) => {
            const reader = new FileReader()
            reader.onload = () => {
              const src = String(reader.result)
              editor.update(() => {
                const imageNode = $createImageNode({ src, altText: file.name })
                $insertNodes([imageNode])
              })
            }
            reader.readAsDataURL(file)
          })
          return true
        }
        return false
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor])

  useEffect(() => {
    return editor.registerCommand(
      DROP_COMMAND,
      (event: DragEvent) => {
        const files = Array.from(event.dataTransfer?.files || [])
        const images = files.filter((f) => f.type.startsWith('image/'))
        if (images.length > 0) {
          event.preventDefault()
          images.forEach((file) => {
            const reader = new FileReader()
            reader.onload = () => {
              const src = String(reader.result)
              editor.update(() => {
                const imageNode = $createImageNode({ src, altText: file.name })
                $insertNodes([imageNode])
              })
            }
            reader.readAsDataURL(file)
          })
          return true
        }
        return false
      },
      COMMAND_PRIORITY_LOW
    )
  }, [editor])

  return null
}

function Initializer({ initialValue, onInitialized }: { initialValue: string; onInitialized: () => void }) {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    editor.update(() => {
      const root = $getRoot()
      // Replace content with initial content (HTML or plain text)
      const value = initialValue ?? ''
      root.clear()

      let nodes = [] as any[]
      const looksLikeHtml = /<\w+[\s\S]*>/i.test(value)

      if (looksLikeHtml) {
        const parser = new DOMParser()
        const dom = parser.parseFromString(value, 'text/html')
        nodes = $generateNodesFromDOM(editor, dom)
      }

      // Fallback for plain text or empty HTML parse
      if (!looksLikeHtml || nodes.length === 0) {
        const paragraph = $createParagraphNode()
        paragraph.append($createTextNode(value))
        nodes = [paragraph]
      }

      root.append(...nodes)
    })
    onInitialized()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return null
}

// Utility function to get HTML content from editor state
export function getHTMLFromEditorState(editorState: EditorState): string {
  return editorState.read(() => {
    const root = $getRoot()
    return root.getTextContent() // This would need proper HTML serialization
  })
}

// Utility function to get plain text from editor state  
export function getPlainTextFromEditorState(editorState: EditorState): string {
  return editorState.read(() => {
    const root = $getRoot()
    return root.getTextContent()
  })
}

export default LexicalRichTextEditor

// Force caret to end on focus for Lexical editor by a small plugin
function CaretToEndOnFocusPlugin(): null {
  const [editor] = useLexicalComposerContext()
  useEffect(() => {
    const onFocus = () => {
      editor.update(() => {
        try {
          const root = $getRoot()
          const last = root.getLastChild()
          // Select end of document
          if (last) {
            last.selectEnd()
          } else {
            root.selectEnd()
          }
        } catch {}
      })
    }
    const rootElem = editor.getRootElement()
    rootElem?.addEventListener('focus', onFocus, true)
    return () => rootElem?.removeEventListener('focus', onFocus, true)
  }, [editor])
  return null
}